/**
 * MathXY - AI-Powered Adaptive Math Learning Platform
 * Copyright (c) 2025 Srilesh Kannoly. All rights reserved.
 * Licensed under the MIT License. See LICENSE file for details.
 */

import { useState, useEffect, Suspense, lazy } from 'react';

import { Button } from './components/Button';
import { HeaderCard } from './components/HeaderCard';
import { HeaderStat } from './components/HeaderStat';
import { Icon } from './components/Icon';
import { TOPIC_CONFIGS } from './config/topicConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PracticeProvider, usePractice } from './contexts/PracticeContext';
import { useCacheManager } from './hooks/useCacheManager';
import type { LegacyTopicSettings } from './services/supabaseService';
import type { TopicType } from './types';
import { trackScreenView } from './utils/analytics';
import './App.css';

const PracticeComponent = lazy(() =>
  import('./components/PracticeComponent').then((m) => ({ default: m.PracticeComponent }))
);
const TopicSelectionScreen = lazy(() =>
  import('./components/TopicSelectionScreen').then((m) => ({ default: m.TopicSelectionScreen }))
);
const TopicSettingsScreen = lazy(() =>
  import('./components/TopicSettingsScreen').then((m) => ({ default: m.TopicSettingsScreen }))
);
const LeaderboardScreen = lazy(() =>
  import('./components/LeaderboardScreen').then((m) => ({ default: m.LeaderboardScreen }))
);
const StatisticsScreen = lazy(() =>
  import('./components/StatisticsScreen').then((m) => ({ default: m.StatisticsScreen }))
);
const SettingsScreen = lazy(() =>
  import('./components/SettingsScreen').then((m) => ({ default: m.SettingsScreen }))
);
const PWATestScreen = lazy(() =>
  import('./components/PWATestScreen').then((m) => ({ default: m.PWATestScreen }))
);
const AboutScreen = lazy(() =>
  import('./components/AboutScreen').then((m) => ({ default: m.AboutScreen }))
);

const ParentAuthScreen = lazy(() =>
  import('./screens/ParentAuthScreen').then((m) => ({ default: m.ParentAuthScreen }))
);
const ParentDashboard = lazy(() =>
  import('./screens/ParentDashboard').then((m) => ({ default: m.ParentDashboard }))
);
const AdminDashboard = lazy(() =>
  import('./screens/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
);

type Screen =
  | 'auth'
  | 'parent-dashboard'
  | 'admin-dashboard'
  | 'topic-selection'
  | 'practice'
  | 'leaderboard'
  | 'statistics'
  | 'settings'
  | 'pwa-test'
  | 'about';

function TopicSelection({ onNavigate }: { onNavigate: (screen: Screen) => void }): JSX.Element {
  const { currentProfile, activeStudent, isGuest, signOut, switchToParent } = useAuth();
  const { incompleteSession, clearIncompleteSession, resumeSession } = usePractice();
  const [selectedTopic, setSelectedTopic] = useState<TopicType | null>(null);
  const [settingsForTopic, setSettingsForTopic] = useState<TopicType | null>(null);
  const [topicSettings, setTopicSettings] = useState<Partial<LegacyTopicSettings> | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);

  // Use active student profile if parent has switched to student mode, otherwise use current profile
  const displayProfile = activeStudent || currentProfile;

  // Check for incomplete session on mount
  useEffect(() => {
    if (incompleteSession && incompleteSession.totalQuestions > 0) {
      setShowRecoveryDialog(true);
    }
  }, [incompleteSession]);

  const handleResumeSession = (): void => {
    if (incompleteSession) {
      resumeSession(); // Use context method to properly resume
      setSelectedTopic(incompleteSession.topicType);
      setShowRecoveryDialog(false);
    }
  };

  const handleDiscardSession = (): void => {
    clearIncompleteSession();
    setShowRecoveryDialog(false);
  };

  const handleExitClick = (): void => {
    if (activeStudent) {
      // Show confirmation when in student mode
      setShowExitConfirm(true);
    } else {
      // Direct sign out for guest or parent mode
      void signOut();
    }
  };

  const handleConfirmExit = async (): Promise<void> => {
    setShowExitConfirm(false);
    await switchToParent(); // Return to parent dashboard
    onNavigate('parent-dashboard');
  };

  // Show settings screen when topic selected
  if (settingsForTopic && !selectedTopic) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TopicSettingsScreen
          topicType={settingsForTopic}
          onStartPractice={(settings): void => {
            setTopicSettings(settings);
            setSelectedTopic(settingsForTopic);
            setSettingsForTopic(null);
          }}
          onBack={(): void => setSettingsForTopic(null)}
        />
      </Suspense>
    );
  }

  // Show practice component when ready
  if (selectedTopic) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <PracticeComponent
          topicType={selectedTopic}
          settings={topicSettings ?? undefined}
          onExit={(): void => {
            setSelectedTopic(null);
            setTopicSettings(null);
          }}
          onBackToSettings={(): void => {
            setSelectedTopic(null);
            setSettingsForTopic(selectedTopic);
          }}
          onGoToStatistics={(): void => {
            // Switch back to parent mode, then go straight to Statistics.
            void (async (): Promise<void> => {
              await switchToParent();
              setSelectedTopic(null);
              setTopicSettings(null);
              onNavigate('statistics');
            })();
          }}
        />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-surface p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <HeaderCard
          left={
            <>
              <div className="text-4xl shrink-0">{displayProfile?.avatar}</div>
              <div className="text-left min-w-0">
                <h2 className="text-title-lg font-semibold truncate">
                  {displayProfile?.displayName}
                </h2>
              </div>
            </>
          }
          right={
            <>
              {displayProfile && 'totalPoints' in displayProfile && (
                <>
                  <HeaderStat
                    label="Points"
                    value={displayProfile.totalPoints}
                    valueClassName="text-warning"
                  />
                  <HeaderStat
                    label="Accuracy"
                    value={`${displayProfile.accuracy?.toFixed(1) || 0}%`}
                    valueClassName="text-success"
                  />
                </>
              )}
              <Button onClick={handleExitClick} variant="outlined" size="sm">
                {activeStudent ? 'Exit' : isGuest ? 'Exit' : 'Sign Out'}
              </Button>
            </>
          }
        />
      </div>

      {/* Navigation Menu */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Hide Leaderboard and Statistics when practicing as student or guest */}
          {!activeStudent && !isGuest && (
            <>
              <button
                onClick={() => onNavigate('leaderboard')}
                className="card p-4 hover:shadow-glow transition-all text-center"
              >
                <div className="text-3xl mb-1">
                  <Icon name="crown" size={40} className="text-warning" />
                </div>
                <div className="text-sm font-semibold">Leaderboard</div>
              </button>
              <button
                onClick={() => onNavigate('statistics')}
                className="card p-4 hover:shadow-glow transition-all text-center"
              >
                <div className="text-3xl mb-1">
                  <Icon name="stats" size={40} className="text-primary" />
                </div>
                <div className="text-sm font-semibold">Statistics</div>
              </button>
            </>
          )}
          {/* Hide Settings, PWA Test, and About when practicing as student or guest */}
          {!activeStudent && !isGuest && (
            <>
              <button
                onClick={() => onNavigate('settings')}
                className="card p-4 hover:shadow-glow transition-all text-center"
              >
                <div className="text-3xl mb-1">
                  <Icon name="settings" size={40} className="text-secondary" />
                </div>
                <div className="text-sm font-semibold">Settings</div>
              </button>
              <button
                onClick={() => onNavigate('pwa-test')}
                className="card p-4 hover:shadow-glow transition-all text-center"
              >
                <div className="text-3xl mb-1">
                  <Icon name="science" size={40} className="text-success" />
                </div>
                <div className="text-sm font-semibold">PWA Test</div>
              </button>
              <button
                onClick={() => onNavigate('about')}
                className="card p-4 hover:shadow-glow transition-all text-center"
              >
                <div className="text-3xl mb-1">
                  <Icon name="info" size={40} className="text-error" />
                </div>
                <div className="text-sm font-semibold">About</div>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Topic Selection */}
      <Suspense fallback={<LoadingScreen />}>
        <TopicSelectionScreen
          onSelectTopic={setSettingsForTopic}
          allowedTopics={activeStudent?.allowedTopics}
        />
      </Suspense>

      {/* Exit Confirmation Dialog */}
      {showExitConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-modal-title"
        >
          <div className="card p-6 max-w-md mx-4">
            <h3 id="exit-modal-title" className="text-title-lg font-semibold mb-4">
              Return to Dashboard?
            </h3>
            <p className="text-text-secondary mb-6">
              Are you sure you want to exit? You'll return to the parent dashboard.
            </p>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setShowExitConfirm(false)} variant="outlined">
                Cancel
              </Button>
              <Button onClick={() => void handleConfirmExit()}>Exit</Button>
            </div>
          </div>
        </div>
      )}

      {/* Session Recovery Dialog */}
      {showRecoveryDialog && incompleteSession && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="recovery-modal-title"
        >
          <div className="card p-6 max-w-md mx-4">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">📝</div>
              <h3 id="recovery-modal-title" className="text-title-lg font-semibold">
                Continue Your Practice?
              </h3>
            </div>
            <p className="text-text-secondary text-center mb-4">
              You have an unfinished{' '}
              <span className="text-primary font-medium">
                {TOPIC_CONFIGS[incompleteSession.topicType]?.name || incompleteSession.topicType}
              </span>{' '}
              session.
            </p>
            <div className="bg-surface-container rounded-xl p-4 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-headline-sm font-semibold text-primary">
                    {incompleteSession.totalQuestions}
                  </div>
                  <div className="text-xs text-text-muted">Questions</div>
                </div>
                <div>
                  <div className="text-headline-sm font-semibold text-success">
                    {incompleteSession.correctAnswers}
                  </div>
                  <div className="text-xs text-text-muted">Correct</div>
                </div>
                <div>
                  <div className="text-headline-sm font-semibold text-warning">
                    {incompleteSession.pointsEarned}
                  </div>
                  <div className="text-xs text-text-muted">Points</div>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={handleDiscardSession} variant="outlined" className="flex-1">
                Start Fresh
              </Button>
              <Button onClick={handleResumeSession} className="flex-1">
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingScreen(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="flex items-center gap-1">
        <div
          className="w-3 h-3 bg-primary rounded-full bounce-dot"
          style={{ animationDelay: '0ms' }}
        ></div>
        <div
          className="w-3 h-3 bg-secondary rounded-full bounce-dot"
          style={{ animationDelay: '150ms' }}
        ></div>
        <div
          className="w-3 h-3 bg-error rounded-full bounce-dot"
          style={{ animationDelay: '300ms' }}
        ></div>
      </div>
    </div>
  );
}

function AppContent(): JSX.Element {
  const { user, isLoading, isParent, activeStudent, isAuthenticated } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('auth');

  // Check for service worker updates on initial load only
  // Don't check on every visibility change - too aggressive
  useCacheManager({
    clearOnVisibilityChange: false,
    clearOnFocus: false,
  });

  // Track screen navigation in Google Analytics
  useEffect(() => {
    trackScreenView(currentScreen);
  }, [currentScreen]);

  // Handle routing based on authentication state changes
  useEffect(() => {
    // Wait for loading to complete
    if (isLoading) return;

    // Navigate back to auth if signed out
    if (!user && currentScreen !== 'auth') {
      setCurrentScreen('auth');
      return;
    }

    // Admin user -> redirect to admin dashboard
    if (isAuthenticated && user?.role === 'admin') {
      if (currentScreen === 'auth') {
        setCurrentScreen('admin-dashboard');
        return;
      }
    }

    // Parent just signed in -> redirect to parent dashboard
    if (isAuthenticated && isParent && !activeStudent) {
      if (currentScreen === 'auth') {
        setCurrentScreen('parent-dashboard');
        return;
      }
    }

    // Navigate to topic selection when parent switches to student from dashboard
    if (activeStudent && currentScreen === 'parent-dashboard') {
      setCurrentScreen('topic-selection');
      return;
    }

    // Navigate to topic selection after guest sign in
    if (user && (user.isGuest || activeStudent) && currentScreen === 'auth') {
      setCurrentScreen('topic-selection');
      return;
    }
  }, [user, isLoading, isParent, activeStudent, isAuthenticated, currentScreen]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // If not logged in, show auth screen
  if (!user) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ParentAuthScreen />
      </Suspense>
    );
  }

  // User is logged in - show appropriate screen
  if (currentScreen === 'auth') {
    // If somehow on auth screen but logged in, redirect based on role
    if (user.role === 'admin') {
      return (
        <Suspense fallback={<LoadingScreen />}>
          <AdminDashboard />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ParentDashboard />
      </Suspense>
    );
  }

  // Show parent dashboard
  if (currentScreen === 'parent-dashboard') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ParentDashboard />
      </Suspense>
    );
  }

  // Show admin dashboard
  if (currentScreen === 'admin-dashboard') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AdminDashboard />
      </Suspense>
    );
  }

  switch (currentScreen) {
    case 'topic-selection':
      return <TopicSelection onNavigate={setCurrentScreen} />;
    case 'leaderboard':
      return (
        <div className="min-h-screen bg-surface p-4">
          <div className="max-w-4xl mx-auto">
            <Suspense fallback={<LoadingScreen />}>
              <LeaderboardScreen onClose={() => setCurrentScreen('topic-selection')} />
            </Suspense>
          </div>
        </div>
      );
    case 'statistics':
      return (
        <div className="min-h-screen bg-surface p-4">
          <div className="max-w-4xl mx-auto">
            <Suspense fallback={<LoadingScreen />}>
              <StatisticsScreen onClose={() => setCurrentScreen('topic-selection')} />
            </Suspense>
          </div>
        </div>
      );
    case 'settings':
      return (
        <div className="min-h-screen bg-surface p-4">
          <div className="max-w-4xl mx-auto">
            <Suspense fallback={<LoadingScreen />}>
              <SettingsScreen onClose={() => setCurrentScreen('topic-selection')} />
            </Suspense>
          </div>
        </div>
      );
    case 'pwa-test':
      return (
        <Suspense fallback={<LoadingScreen />}>
          <PWATestScreen onClose={() => setCurrentScreen('topic-selection')} />
        </Suspense>
      );
    case 'about':
      return (
        <Suspense fallback={<LoadingScreen />}>
          <AboutScreen onBack={() => setCurrentScreen('topic-selection')} />
        </Suspense>
      );
    default:
      return <TopicSelection onNavigate={setCurrentScreen} />;
  }
}

function App(): JSX.Element {
  return (
    <AuthProvider>
      <PracticeProvider>
        <AppContent />
      </PracticeProvider>
    </AuthProvider>
  );
}

export default App;
